import React, { useState, useEffect, useCallback } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Checkbox,
  Paper,
  Box,
  Typography,
  Alert,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import HoursInput from '../../components/common/HoursInput';
import { submitTimecard } from '../../services/timecardService';
import { checkMonthBoundary } from '../../utils/dateUtils';
import { getCurrentUser, getUserPtoHours } from '../../services/userService';
import { useAuth } from '../../context/AuthContext';
import { format } from 'date-fns';

const EditTimecardDialog = ({ 
    open, 
    onClose, 
    timecard,
    onSave,
    onSubmit
}) => {
    const { user: currentUser } = useAuth();
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));
    
    // Helper function to parse UTC date string
    const parseUTCDate = (dateString) => {
        const date = new Date(dateString);
        return new Date(
            date.getUTCFullYear(),
            date.getUTCMonth(),
            date.getUTCDate(),
            0, 0, 0, 0
        );
    };
    
    // Format date - mobile uses "MM/dd/yy" format, desktop uses "MMM d, yyyy" format
    const formatDate = (dateString) => {
        const date = parseUTCDate(dateString);
        if (isMobile) {
            return format(date, 'MM/dd/yy'); // e.g., "06/08/25"
        }
        return format(date, 'MMM d, yyyy'); // e.g., "Jun 8, 2025"
    };
    const [days, setDays] = useState([]);
    const [, setIsCertified] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [monthBoundaryInfo, setMonthBoundaryInfo] = useState(null);
    const [ptoBalance, setPtoBalance] = useState(null);
    const [originalTimecardHours, setOriginalTimecardHours] = useState(0);
    const [showPtoWarning, setShowPtoWarning] = useState(false);
    const [hasHoursError, setHasHoursError] = useState(false);

    // Fetch user's PTO balance
    const fetchPtoBalance = useCallback(async () => {
        try {
            const userData = await getCurrentUser();
            const ptoHours = await getUserPtoHours(userData._id);
            
            // Calculate available PTO hours
            const available = (
                parseFloat(ptoHours.yearStart || 0) +
                parseFloat(ptoHours.ytdAccumulated || 0) -
                parseFloat(ptoHours.ytdUsed || 0)
            ).toFixed(2);
            
            setPtoBalance(parseFloat(available));
        } catch (error) {
            console.error('Error fetching PTO balance:', error);
            setPtoBalance(null);
        }
    }, []);

    // Initialize days and check month boundary when timecard changes or dialog opens
    useEffect(() => {
        if (timecard && open) {
            // Check for month boundary using the raw date strings
            const boundaryInfo = checkMonthBoundary(
                timecard.startDate,
                timecard.endDate
            );
            setMonthBoundaryInfo(boundaryInfo);
            
            // Calculate original timecard hours for PTO validation
            // This is needed to add back to the balance when checking if new hours exceed available
            const originalHours = timecard.days.reduce((sum, day) => sum + parseFloat(day.hours || 0), 0);
            setOriginalTimecardHours(originalHours);
            
            // Map the days with proper formatting, preserving original order
            const formattedDays = timecard.days.map(day => ({
                ...day,
                hours: parseFloat(day.hours || 0).toFixed(2),
                comments: day.comments || '',
                // Preserve the actual certified and submitted states from the backend
                // Don't override based on timecard status - trust the backend data
                certified: day.certified || false,
                submitted: day.submitted || false
            }));

            setDays(formattedDays);
            
            // Fetch PTO balance if this is a PTO project
            if (timecard.project && (
                timecard.project.name.includes('Leave') || 
                timecard.project.name.includes('ADMIN_PTO_SSL_LEAVE')
            )) {
                fetchPtoBalance();
            } else {
                // Reset PTO balance if not a PTO project
                setPtoBalance(null);
            }
        } else if (!open) {
            // Reset all state when dialog is closed
            setDays([]);
            setMonthBoundaryInfo(null);
            setPtoBalance(null);
            setOriginalTimecardHours(0);
            setShowPtoWarning(false);
            setHasHoursError(false);
            setIsSubmitting(false);
        }
    }, [timecard, open, fetchPtoBalance]);

    // Update certification status when days change
    useEffect(() => {
        setIsCertified(days.some(day => day.certified));
    }, [days]);

    const isDayDisabled = (day, index) => {
        // First check if this is a month boundary week
        if (monthBoundaryInfo?.isMonthBoundary) {
            const isNewMonthDay = monthBoundaryInfo.newMonthDays.some(d => 
                d === day.date
            );

            // For new month days in a boundary week
            if (isNewMonthDay) {
                // If timecard was previously approved and is now uncertified by admin,
                // allow editing all days
                if (timecard.wasEverApproved && 
                    (timecard.status === 'Uncertified' || timecard.status === 'Partially Certified')) {
                    return false;
                }

                // IMPORTANT: First check if THIS new month day itself is already certified and submitted
                // If so, it should be disabled regardless of end-of-month status
                if (day.certified && day.submitted) {
                    return true;
                }

                // Otherwise enforce month boundary validation
                const endOfMonthDates = monthBoundaryInfo.endOfMonthDays;
                const endOfMonthDays = days.filter(d => 
                    endOfMonthDates.includes(d.date)
                );

                // Check if end-of-month days are certified/submitted OR approved
                // Approved days should be considered as valid for allowing new month entry
                const allEndOfMonthCertifiedOrApproved = endOfMonthDays.every(d => 
                    (d.certified && d.submitted) || d.approved
                );

                // Disable new month days if end-of-month days aren't certified/submitted or approved
                return !allEndOfMonthCertifiedOrApproved;
            }
        }

        // For non-boundary weeks or end-of-month days in boundary weeks
        if (timecard.wasEverApproved && 
            (timecard.status === 'Uncertified' || timecard.status === 'Partially Certified')) {
            return false;
        }

        // Check if day is approved (approved days should be disabled from further editing)
        if (day.approved) {
            return true;
        }

        // Check if day is certified and submitted (but not approved)
        if (day.certified && day.submitted) {
            return true;
        }

        return false;
    };

    // Check if total hours exceed PTO balance
    useEffect(() => {
        if (timecard && ptoBalance !== null && (
            timecard.project?.name.includes('Leave') || 
            timecard.project?.name.includes('ADMIN_PTO_SSL_LEAVE')
        )) {
            // For boundary weeks, only count hours from new month days
            // For non-boundary weeks, count all hours
            let totalHours;
            
            if (monthBoundaryInfo?.isMonthBoundary) {
                // Only count hours from new month days
                const newMonthDates = monthBoundaryInfo.newMonthDays;
                totalHours = days
                    .filter(day => newMonthDates.includes(day.date))
                    .reduce((sum, day) => sum + parseFloat(day.hours || 0), 0);
            } else {
                // Count all hours for non-boundary weeks
                totalHours = days.reduce((sum, day) => sum + parseFloat(day.hours || 0), 0);
            }
            
            // Add back the original timecard hours to the balance since they were already deducted
            // This gives us the effective available balance for this edit
            const effectiveBalance = ptoBalance + originalTimecardHours;
            
            // Show warning if hours exceed the effective balance
            setShowPtoWarning(totalHours > effectiveBalance);
        } else {
            setShowPtoWarning(false);
        }
    }, [days, ptoBalance, originalTimecardHours, timecard, monthBoundaryInfo]);

    // Check for hours validation errors
    useEffect(() => {
        const hasError = days.some(day => parseFloat(day.hours || 0) > 24);
        setHasHoursError(hasError);
    }, [days]);

    const handleHoursChange = (index, value) => {
        const newDays = [...days];
        // Check if day should be disabled
        if (isDayDisabled(days[index], index)) {
            return;
        }
        // Ensure the value is a valid number and not negative
        const hours = Math.max(0, parseFloat(value) || 0).toFixed(2);
        newDays[index] = { ...newDays[index], hours };
        setDays(newDays);
    };

    const handleCommentsChange = (index, value) => {
        const newDays = [...days];
        // Check if day should be disabled
        if (isDayDisabled(days[index], index)) {
            return;
        }
        newDays[index] = { ...newDays[index], comments: value };
        setDays(newDays);
    };

    const handleCertifyChange = (index, checked) => {
        const newDays = [...days];
        // Check if day should be disabled
        if (isDayDisabled(days[index], index)) {
            return;
        }
        newDays[index] = { ...newDays[index], certified: checked };
        setDays(newDays);
    };

    const handleCertifyAll = (checked) => {
        // Only allow certifying days that aren't disabled
        const newDays = days.map((day, index) => ({
            ...day,
            certified: isDayDisabled(day, index) ? day.certified : checked
        }));
        setDays(newDays);
    };

    const handleSave = () => {
        // If this is a PTO project and hours exceed balance, show warning
        if (timecard && ptoBalance !== null && (
            timecard.project?.name.includes('Leave') || 
            timecard.project?.name.includes('ADMIN_PTO_SSL_LEAVE')
        )) {
            // For boundary weeks, only count hours from new month days
            // For non-boundary weeks, count all hours
            let totalHours;
            
            if (monthBoundaryInfo?.isMonthBoundary) {
                // Only count hours from new month days
                const newMonthDates = monthBoundaryInfo.newMonthDays;
                totalHours = days
                    .filter(day => newMonthDates.includes(day.date))
                    .reduce((sum, day) => sum + parseFloat(day.hours || 0), 0);
            } else {
                // Count all hours for non-boundary weeks
                totalHours = days.reduce((sum, day) => sum + parseFloat(day.hours || 0), 0);
            }
            
            // Add back the original timecard hours to the balance since they were already deducted
            const effectiveBalance = ptoBalance + originalTimecardHours;
            
            // Show warning if hours exceed the effective balance
            const exceedsPtoBalance = totalHours > effectiveBalance;
            setShowPtoWarning(exceedsPtoBalance);
            
            // Only proceed with save if hours don't exceed balance
            if (exceedsPtoBalance) {
                return; // Prevent saving
            }
        }
        
        // Save the days with their current certification state preserved
        // This allows users to check certification boxes and save them before submitting
        onSave({ ...timecard, days });
        onClose();
    };

    const handleSubmit = async () => {
        try {
            setIsSubmitting(true);
            
            // First save the current state
            await onSave({ ...timecard, days });
            
            // Then submit the timecard and get the updated state
            const updatedTimecard = await submitTimecard(timecard._id);
            
            console.log('Timecard submitted:', {
                id: updatedTimecard._id,
                status: updatedTimecard.status,
                days: updatedTimecard.days.map(d => ({
                    date: d.date,
                    certified: d.certified,
                    submitted: d.submitted
                }))
            });
            
            // Notify parent of submission with updated timecard
            await onSubmit(updatedTimecard);
            
            // Don't close dialog here - let the parent component handle it
            // onClose();
        } catch (error) {
            console.error('Error submitting timecard:', error);
        } finally {
            setIsSubmitting(false);
        }
    };

    const areAllDaysCertified = days.length > 0 && days.every(day => day.certified);
    const areSomeDaysCertified = days.some(day => day.certified);
    // Only show "Certify Days" button for newly certified days that aren't approved yet
    // Approved days should not trigger the button since they're already processed
    const hasUnsubmittedCertifiedDays = days.some(day => day.certified && !day.submitted && !day.approved);

    if (!timecard) return null;

    return (
        <Dialog 
            open={open} 
            onClose={onClose}
            maxWidth="md"
            fullWidth
            sx={{
                '& .MuiDialog-paper': {
                    bgcolor: 'background.paper',
                },
                '& .MuiBackdrop-root': {
                    backgroundColor: 'rgba(0, 0, 0, 0.7)',
                },
            }}
        >
            <DialogTitle>
                {isMobile ? (
                    <Box>
                        <Typography variant="h6" component="div">Edit Timecard</Typography>
                        <Typography variant="subtitle2" color="textSecondary" sx={{ mt: 0.5 }}>
                            {timecard.project?.name}
                        </Typography>
                        <Typography variant="subtitle2" color="textSecondary">
                            {currentUser ? `${currentUser.firstName} ${currentUser.lastName}` : 'Unknown User'}
                        </Typography>
                    </Box>
                ) : (
                    `Edit Timecard - ${timecard.project?.name} - ${currentUser ? `${currentUser.firstName} ${currentUser.lastName}` : 'Unknown User'}`
                )}
            </DialogTitle>
            <DialogContent>
                {showPtoWarning && (
                    <Alert severity="warning" sx={{ mb: 2 }}>
                        There are only {(ptoBalance + originalTimecardHours).toFixed(2)} remaining hours available to use.
                    </Alert>
                )}
                
                {hasHoursError && (
                    <Alert severity="error" sx={{ mb: 2 }}>
                        Daily hours cannot exceed 24 hours. Please correct the highlighted fields.
                    </Alert>
                )}
                
                {monthBoundaryInfo?.isMonthBoundary && !timecard.wasEverApproved && (() => {
                    // Check if all end-of-month days are certified/submitted or approved
                    const endOfMonthDates = monthBoundaryInfo.endOfMonthDays;
                    const endOfMonthDays = days.filter(d => 
                        endOfMonthDates.includes(d.date)
                    );
                    const allEndOfMonthCertifiedOrApproved = endOfMonthDays.every(d => 
                        (d.certified && d.submitted) || d.approved
                    );

                    // Check if there are uncertified days in the new month
                    const newMonthDates = monthBoundaryInfo.newMonthDays;
                    const newMonthDays = days.filter(d => 
                        newMonthDates.includes(d.date)
                    );
                    const hasUncertifiedNewMonthDays = newMonthDays.some(d => !d.certified);

                    // Show warning if end-of-month days are certified/submitted/approved but new month days aren't all certified
                    if (allEndOfMonthCertifiedOrApproved && hasUncertifiedNewMonthDays) {
                        return (
                            <Alert severity="warning" sx={{ mb: 2 }}>
                                There are still remaining days to certify and submit to complete the entire week certification!
                            </Alert>
                        );
                    }

                    // Only show info alert if end-of-month days aren't certified/submitted or approved
                    if (!allEndOfMonthCertifiedOrApproved) {
                        return (
                            <Alert severity="info" sx={{ mb: 2 }}>
                                You must certify and submit all end-of-month days before entering hours for the new month.
                            </Alert>
                        );
                    }
                    return null;
                })()}
                <Box sx={{ mb: 2 }}>
                    <Typography variant="subtitle1">
                        Week of {format(parseUTCDate(timecard.startDate), 'MMM d')} - {format(parseUTCDate(timecard.endDate), 'MMM d, yyyy')}
                    </Typography>
                    <Typography variant="subtitle2" color="textSecondary">
                        Status: {timecard.status || 'Uncertified'}
                    </Typography>
                </Box>
                <TableContainer component={Paper}>
                    <Table 
                        size="small"
                        sx={isMobile ? {
                            '& .MuiTableCell-root:nth-of-type(1)': { width: '15% !important' },
                            '& .MuiTableCell-root:nth-of-type(2)': { width: '15% !important' },
                            '& .MuiTableCell-root:nth-of-type(3)': { width: '8% !important' },
                            '& .MuiTableCell-root:nth-of-type(4)': { width: '15% !important' },
                            '& .MuiTableCell-root:nth-of-type(5)': { width: '10% !important' },
                            tableLayout: 'fixed !important',
                            width: '100% !important'
                        } : {}}
                    >
                        <TableHead>
                            <TableRow>
                                <TableCell sx={isMobile ? { fontSize: '0.75rem', width: '15%' } : {}}>Date</TableCell>
                                <TableCell sx={isMobile ? { fontSize: '0.75rem', width: '15%' } : {}}>Hours</TableCell>
                                <TableCell sx={isMobile ? { fontSize: '0.75rem', width: '8%' } : {}}>{isMobile ? 'Day' : 'Day of Week'}</TableCell>
                                <TableCell sx={isMobile ? { fontSize: '0.75rem', width: '15%' } : {}}>Comments</TableCell>
                                <TableCell align="center" sx={isMobile ? { fontSize: '0.75rem', width: '10%' } : {}}>
                                    Certify
                                    <Checkbox
                                        checked={areAllDaysCertified}
                                        indeterminate={areSomeDaysCertified && !areAllDaysCertified}
                                        onChange={(e) => handleCertifyAll(e.target.checked)}
                                        tabIndex={-1}
                                        sx={isMobile ? { 
                                            padding: '2px',
                                            minWidth: '32px',
                                            minHeight: '32px',
                                            '& .MuiSvgIcon-root': {
                                                fontSize: '1rem'
                                            }
                                        } : { padding: '0 0 0 4px' }}
                                    />
                                </TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {days.map((day, index) => (
                                <TableRow 
                                    key={day.date}
                                    sx={{
                                        bgcolor: ((day.certified && day.submitted) || day.approved) ? 'action.hover' : 'inherit',
                                        // Add visual indicator for month boundary days
                                        ...(monthBoundaryInfo?.isMonthBoundary && {
                                            borderLeft: (theme) => {
                                                const isEndOfMonth = monthBoundaryInfo.endOfMonthDays.includes(day.date);
                                                const isNewMonth = monthBoundaryInfo.newMonthDays.includes(day.date);
                                                if (isEndOfMonth) {
                                                    return `4px solid ${theme.palette.warning.main}`;
                                                } else if (isNewMonth) {
                                                    return `4px solid ${theme.palette.info.main}`;
                                                }
                                                return 'none';
                                            }
                                        })
                                    }}
                                >
                                    <TableCell sx={isMobile ? { fontSize: '0.75rem', padding: '4px', width: '15%' } : {}}>{formatDate(day.date)}</TableCell>
                                    <TableCell sx={isMobile ? { padding: '2px', width: '20%' } : {}}>
                                        <HoursInput
                                            value={day.hours}
                                            onChange={(value) => handleHoursChange(index, value)}
                                            disabled={isDayDisabled(day, index)}
                                        />
                                    </TableCell>
                                    <TableCell sx={isMobile ? { fontSize: '0.75rem', padding: '4px', width: '15%' } : {}}>{['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'][index]}</TableCell>
                                    <TableCell sx={isMobile ? { padding: '2px', width: '40%' } : {}}>
                                        <TextField
                                            value={day.comments || ''}
                                            onChange={(e) => handleCommentsChange(index, e.target.value)}
                                            size="small"
                                            disabled={isDayDisabled(day, index)}
                                            fullWidth
                                            sx={{
                                                '& .MuiOutlinedInput-root': {
                                                    ...(isMobile && {
                                                        minHeight: 'unset !important',
                                                        height: 'auto !important',
                                                        '& input': {
                                                            padding: '8px 4px !important', // Force exact match to HoursInput
                                                            fontSize: '0.75rem !important',
                                                            height: 'auto !important',
                                                            minHeight: 'unset !important',
                                                        }
                                                    })
                                                }
                                            }}
                                        />
                                    </TableCell>
                                    <TableCell align="center" sx={isMobile ? { padding: '4px', width: '10%' } : {}}>
                                        <Checkbox
                                            checked={day.certified || false}
                                            onChange={(e) => handleCertifyChange(index, e.target.checked)}
                                            disabled={isDayDisabled(day, index)}
                                            tabIndex={-1}
                                            sx={isMobile ? { 
                                                padding: '2px',
                                                minWidth: '32px',
                                                minHeight: '32px',
                                                '& .MuiSvgIcon-root': {
                                                    fontSize: '1rem'
                                                }
                                            } : { padding: '4px' }}
                                        />
                                    </TableCell>
                                </TableRow>
                            ))}
                            {/* Total row */}
                            <TableRow sx={{ 
                                borderTop: '2px solid',
                                borderTopColor: 'divider',
                                bgcolor: '#fff3cd'
                            }}>
                                <TableCell sx={isMobile ? { fontSize: '0.75rem', padding: '4px', width: '15%' } : {}}>Total</TableCell>
                                <TableCell sx={isMobile ? { padding: '2px', width: '20%' } : {}}>
                                    <Box sx={{ 
                                        display: 'flex',
                                        justifyContent: 'center',
                                        alignItems: 'center',
                                        minHeight: isMobile ? '32px' : '40px',
                                        fontSize: isMobile ? '0.75rem' : '1rem',
                                        fontWeight: 'bold'
                                    }}>
                                        {days.reduce((sum, day) => sum + parseFloat(day.hours || 0), 0).toFixed(2)}
                                    </Box>
                                </TableCell>
                                <TableCell sx={isMobile ? { fontSize: '0.75rem', padding: '4px', width: '15%' } : {}}></TableCell>
                                <TableCell sx={isMobile ? { padding: '2px', width: '40%' } : {}}></TableCell>
                                <TableCell sx={isMobile ? { padding: '4px', width: '10%' } : {}}></TableCell>
                            </TableRow>
                        </TableBody>
                    </Table>
                </TableContainer>
            </DialogContent>
            <DialogActions sx={{ 
                display: 'flex', 
                justifyContent: 'center',
                alignItems: 'center',
                flexDirection: 'column',
                p: 3,
                pt: 0
            }}>
                <Box sx={{ 
                    display: 'flex',
                    justifyContent: 'flex-end',
                    gap: 1,
                    width: '100%'
                }}>
                    <Button onClick={onClose}>Cancel</Button>
                    <Button 
                        onClick={hasUnsubmittedCertifiedDays ? handleSubmit : handleSave} 
                        variant="contained" 
                        color={hasUnsubmittedCertifiedDays ? undefined : "primary"}
                        disabled={showPtoWarning || hasHoursError || (hasUnsubmittedCertifiedDays && isSubmitting)}
                        sx={hasUnsubmittedCertifiedDays ? {
                            backgroundColor: '#2196f3',
                            color: '#ffffff',
                            '&:hover': {
                                backgroundColor: '#1976d2',
                            },
                            '&:disabled': {
                                backgroundColor: '#2196f3',
                                color: '#ffffff',
                                opacity: 0.6,
                            }
                        } : {}}
                    >
                        {hasUnsubmittedCertifiedDays 
                            ? (isSubmitting ? 'Submitting...' : 'Submit Certified Days')
                            : 'Save'
                        }
                    </Button>
                </Box>
                {hasUnsubmittedCertifiedDays && (
                    <Typography variant="body2" color="text.secondary" align="center" sx={{ mt: 1 }}>
                        I certify that the submitted timecard data is accurate to the best of my knowledge.
                    </Typography>
                )}
            </DialogActions>
        </Dialog>
    );
};

export default EditTimecardDialog;
